import * as React from "react"
import { useTranslation } from "react-i18next"
import styled from "styled-components"
import { HorizontalSpacer, Spacer } from "../atoms/spacers"
import { theme } from "../theme"
import H3 from "../atoms/typography/H3"
import H4 from "../atoms/typography/H4"
import Dropdown, { Option, optionsMap } from "../atoms/inputs/Dropdown"
import { Col, Container, Row } from "react-grid-system"
import { PAGINATION_VALUE } from "../../utils/design-helpers"
import { Key } from "react-aria-components"

export interface PaginationProps {
  pages: number
  currentPageIndex: number
  onClickPage: (index: number) => void
  itemsPerPage?: number
  onItemsPerPageChange?: (itemsPerPage: number) => void
  itemsPerPageValues: number[]
}

const NumbersContainer = styled.div`
  display: flex;
  align-items: center;
`

const Number = styled.div<{ selected?: boolean }>`
  padding: 0.5rem;
  width: 48px;
  height: 48px;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 50%;
  cursor: pointer;
  background-color: ${(props) => (props.selected ? props.theme.colors.greyDark : "transparent")};

  :hover {
    filter: brightness(80%);
    background-color: ${(props) => props.theme.colors.blue};
  }
`

const Ellipsis = styled.div`
  width: 48px;
  height: 48px;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 16px;
  color: ${theme.colors.greyDark};
`

const PaginationComponent: React.FunctionComponent<PaginationProps> = ({
  pages,
  currentPageIndex,
  onClickPage,
  itemsPerPage = PAGINATION_VALUE,
  onItemsPerPageChange,
  itemsPerPageValues,
}) => {
  const { t } = useTranslation()

  const MAX_PAGES_TO_DISPLAY = 8

  const onClickNewNumber = (numb: number) => {
    onClickPage(numb)
  }

  const [selectedPageNumberId, setSelectedPageNumberId] = React.useState<Key>(itemsPerPage)

  const changeNumberOfItemsPerPageId = (id: Key) => {
    setSelectedPageNumberId(id)
    onItemsPerPageChange?.(optionsMap(options)[id].value!)
  }

  const pageObjectArray: Array<{ value: number }> = []
  var i = 1

  for (i; i <= pages; i++) {
    const obj = { value: i }
    pageObjectArray.push(obj)
  }

  const renderPageNumbers = () => {
    if (pages <= MAX_PAGES_TO_DISPLAY) {
      return pageObjectArray.map((p) => (
        <Number key={p.value} selected={currentPageIndex === p.value} onClick={() => onClickNewNumber(p.value)}>
          <H4 color={currentPageIndex === p.value ? theme.colors.white : theme.colors.greyDark}>{p.value}</H4>
        </Number>
      ))
    } else {
      // pageNumbers are the pages that are displayed in the pagination component
      const pageNumbers = []

      if (currentPageIndex < 5) {
        // If the user is on a page that is less than page 5, display page 1-5 before adding an ellipsis
        for (let i = 1; i <= 5; i++) {
          pageNumbers.push(i)
        }
        pageNumbers.push("ellipsis-end", pages)
      } else if (currentPageIndex >= pages - 3) {
        // If the user is on of the last pages, display the last 5 pages after the ellipsis
        pageNumbers.push(1, "ellipsis-start")
        for (let i = pages - 4; i <= pages; i++) {
          pageNumbers.push(i)
        }
      } else {
        // Display current page and 2 pages before and after if the user is on a page that isn't one of the first or last pages
        pageNumbers.push(1, "ellipsis-start")
        for (let i = currentPageIndex - 1; i <= currentPageIndex + 1; i++) {
          pageNumbers.push(i)
        }
        pageNumbers.push("ellipsis-end", pages)
      }

      return pageNumbers.map((page, index) => {
        if (page === "ellipsis-start" || page === "ellipsis-end") {
          return <Ellipsis key={index}>...</Ellipsis>
        }

        const selected = currentPageIndex === page

        return (
          <Number key={index} selected={selected} onClick={() => onClickNewNumber(+page)}>
            <H4 color={selected ? theme.colors.white : theme.colors.greyDark}>{page}</H4>
          </Number>
        )
      })
    }
  }

  const options: Option<number>[] = itemsPerPageValues?.map((value) => ({ id: value, value, name: value.toString() }))

  return (
    <Container>
      <Row justify="between">
        <H3>{t("page")}:</H3>
        <HorizontalSpacer spacing={2} />
      </Row>
      <Row>
        <NumbersContainer>{renderPageNumbers()}</NumbersContainer>
        <Spacer spacing={3} />
        {itemsPerPageValues && (
          <Row align="center">
            <H3>Items per page</H3>
            <Col>
              <Dropdown
                label="Items per page"
                onSelectionChange={(id) => changeNumberOfItemsPerPageId(id)}
                options={options}
                selectedKey={selectedPageNumberId}
              />
            </Col>
          </Row>
        )}
      </Row>
    </Container>
  )
}

export default PaginationComponent
